import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { STATUS, STATUS_ORDER } from "@/lib/status";
import { ServiceCalendar } from "@/components/ServiceCalendar";
import { DailyAvailability } from "@/components/DailyAvailability";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Save, CalendarCheck, Info } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export default function Availability() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const focusedService = params.get("service");
  const [services, setServices] = useState([]);
  const [avail, setAvail] = useState({});
  const [myShifts, setMyShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [answered, setAnswered] = useState([]);
  const [notes, setNotes] = useState({});
  const [dirty, setDirty] = useState([]);

  const scheduledByDate = useMemo(() => {
    const m = {};
    myShifts.forEach((a) => {
      const d = a.service?.date;
      if (d) (m[d] ||= []).push({ name: a.service.name, time: `${a.service.start_time}–${a.service.end_time}` });
    });
    return m;
  }, [myShifts]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a, ms] = await Promise.all([
        api.get("/services"),
        api.get("/availability/me?include_responses=true"),
        api.get("/planning/my-shifts").catch(() => ({ data: [] })),
      ]);
      setServices(s.data);
      setAvail(a.data.statuses);
      setAnswered(a.data.answered_ids);
      setNotes(a.data.notes || {});
      setMyShifts(ms.data);
      setDirty([]);
    } catch (e) {
      setServices([]); setAvail({}); setAnswered([]); setNotes({}); setMyShifts([]); setDirty([]);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = (sid, status) => {
    setAvail((a) => ({ ...a, [sid]: status }));
    setDirty((ids) => [...new Set([...ids, sid])]);
  };
  const setNote = (sid, val) => {
    setNotes((n) => ({ ...n, [sid]: val }));
    setDirty((ids) => [...new Set([...ids, sid])]);
  };
  const answerImmediately = async (sid, status, note) => {
    try {
      const item = { service_id: sid, status };
      if (note !== undefined) item.note = note ?? "";
      await api.put("/availability/me", { items: [item] });
      setAvail((a) => ({ ...a, [sid]: status }));
      if (note !== undefined) setNotes((n) => ({ ...n, [sid]: note ?? "" }));
      setAnswered((ids) => [...new Set([...ids, sid])]);
      setDirty((ids) => ids.filter((id) => id !== sid));
    } catch (e) { throw new Error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const items = services.filter((s) => dirty.includes(s.id)).map((s) => ({ service_id: s.id, status: avail[s.id] || "none", note: notes[s.id] ?? "" }));
      await api.put("/availability/me", { items });
      setAnswered((ids) => [...new Set([...ids, ...dirty])]);
      setDirty([]);
      toast.success("Beschikbaarheid opgeslagen");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="availability-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Beschikbaarheid</h1>
          <p className="text-muted-foreground mt-1">Geef per dienst aan of je kunt en wilt werken.</p>
        </div>
      </div>

      <div className="md:hidden">
        <DailyAvailability services={services} availability={avail} answered={answered} notes={notes} onNote={setNote} onAnswer={answerImmediately} onComplete={() => { toast.success("Alles ingevuld — bedankt!"); navigate("/"); }} loading={loading} focusedService={focusedService} scheduled={scheduledByDate} />
      </div>
      <div className="hidden md:flex flex-wrap gap-3">
        {STATUS_ORDER.map((k) => {
          const s = STATUS[k];
          return (
            <span key={k} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${s.badge}`}>
              <s.Icon className="w-3.5 h-3.5" /> {s.label}
            </span>
          );
        })}
      </div>

      <div className="hidden md:block space-y-6">
        <ServiceCalendar key={focusedService || "availability"} services={services} calendarId="availability" loading={loading}
          getStatus={(s) => { const k = avail[s.id] || "none"; return { tone: STATUS[k].tone, label: STATUS[k].label }; }}
          renderDetails={(s) => {
                  const cur = avail[s.id] || "none";
                  return (
                    <Card key={s.id} className="p-4" data-testid={`avail-service-${s.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <p className="font-medium">{s.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {s.start_time}–{s.end_time}{s.location ? ` · ${s.location}` : ""}
                          </p>
                          {scheduledByDate[s.date]?.length > 0 && <p className="mt-1 text-xs font-medium text-red-700 flex items-center gap-1" data-testid={`avail-scheduled-${s.id}`}><CalendarCheck className="w-3.5 h-3.5" />Je staat deze dag al ingepland</p>}
                          {s.notes && <p className="mt-1 text-xs text-amber-700 flex items-start gap-1" data-testid={`avail-svc-note-${s.id}`}><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{s.notes}</p>}
                        </div>
                        <div className="grid grid-cols-4 gap-2 sm:flex">
                          {STATUS_ORDER.map((k) => {
                            const st = STATUS[k];
                            const active = cur === k;
                            return (
                              <button
                                key={k}
                                type="button"
                                onClick={() => setStatus(s.id, k)}
                                data-testid={`avail-${s.id}-${k}`}
                                title={st.label}
                                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                  active ? `${st.badge} ${st.ring}` : "border-slate-200 dark:border-slate-700 text-muted-foreground hover:border-slate-300"
                                }`}
                              >
                                <st.Icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{st.label.split(" ")[0]}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <Textarea value={notes[s.id] || ""} onChange={(e) => setNote(s.id, e.target.value)} placeholder="Korte opmerking voor de planner (optioneel)" rows={2} maxLength={300} className="mt-3" data-testid={`avail-note-${s.id}`} />
                    </Card>
                  );
          }} />

          <div className="sticky bottom-4 flex justify-end">
            <Button onClick={save} disabled={saving || loading || !dirty.length} size="lg" className="shadow-lg" data-testid="availability-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Beschikbaarheid opslaan
            </Button>
          </div>
        </div>
    </div>
  );
}
